import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import Datatable from "../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import Footer from "../../core/common/Footer";
import SearchableSelect from "../../core/common/SearchableSelect";
import { riskMatrixOptions } from "../../core/data/js/common-data";
import { cellWidth } from "../../core/methods/tableCellWidth";
import {
  aadhaarData,
  addressData,
  criminalData,
  criminalLawFirmData,
  educationData,
  employmentData,
  globalDatabaseData,
  internationalEducationData,
  internationalEmploymentData,
  panData,
} from "../../core/data/js/dummy-data";

const RiskMatrix = () => {
  const [showGuide, setShowGuide] = useState<boolean>(true);

  const [selectedRisk, setSelectedRisk] = useState("");
  const [itemList, setItemList] = useState<any[]>([]);

  const handleRiskChange = (value: string) => {
    setSelectedRisk(value);

    switch (value) {
      case "criminal":
        setItemList(criminalData);
        break;
      case "address":
        setItemList(addressData);
        break;
      case "internationalEmployment":
        setItemList(internationalEmploymentData);
        break;
      case "internationalEducation":
        setItemList(internationalEducationData);
        break;
      case "education":
        setItemList(educationData);
        break;
      case "aadharValidation":
        setItemList(aadhaarData);
        break;
      case "drugTestPanel5":
      case "ofac":
      case "aadharKyc":
      case "globalDatabaseCheck":
        setItemList(globalDatabaseData);
        break;
      case "drugTestPanel10":
        setItemList([]);
        break;
      case "criminalCheckViaLawFirm":
        setItemList(criminalLawFirmData);
        break;
      case "panCard":
        setItemList(panData);
        break;
      case "employment":
        setItemList(employmentData);
        break;

      default:
        setItemList([]);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      onCell: () => cellWidth(200),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      onCell: () => cellWidth(180),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      onCell: () => cellWidth(300),
    },
    {
      title: "Check Tagging Status",
      dataIndex: "tagStatus",
      key: "tagStatus",
      onCell: () => cellWidth(150),
      render: (value: string) => (
        <select
          className={`form-select form-select-sm ${
            value === "RED"
              ? "text-danger"
              : value === "YELLOW"
                ? "text-warning"
                : "text-success"
          }`}
        >
          <option>{value}</option>
          <option className="text-danger">RED</option>
          <option className="text-warning">YELLOW</option>
          <option className="text-success">GREEN</option>
        </select>
      ),
    },
    {
      title: "Inactive/Active",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (_: boolean, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={record.status}
            onChange={() => {
              const updatedData = itemList.map((item) =>
                item.key === record.key
                  ? { ...item, status: !item.status }
                  : item,
              );

              setItemList(updatedData);
            }}
          />
        </div>
      ),
    },
    {
      title: "Last Updated Status",
      dataIndex: "updatedDate",
      key: "updatedDate",
      onCell: () => cellWidth(140),
    },
    {
      title: "Log",
      dataIndex: "log",
      key: "log",
      onCell: () => cellWidth(120),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Risk Assessment</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item active">Risk Assessment</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        {/* <GuideContainer
          showGuide={showGuide}
          handleClick={() => setShowGuide((prev) => !prev)}
        /> */}

        <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
          <button
            className="btn border bg-white px-3"
            onClick={() => setShowGuide((prev) => !prev)}
          >
            <i className="ti ti-info-circle-filled fs-16"></i> Guide
          </button>
        </div>

        {showGuide && (
          <div
            style={{
              overflow: "hidden",
              maxHeight: showGuide ? "500px" : "0px",
              transition: "all 0.4s ease",
            }}
          >
            {/* Top Arrow */}
            <div className="text-end pe-3">
              <i
                className="ti ti-triangle-inverted-filled"
                style={{
                  fontSize: "22px",
                  color: "#cfcfcf",
                  lineHeight: 0,
                }}
              ></i>
            </div>

            {/* Guide Box */}
            <div
              className="card border-0 shadow-sm"
              style={{
                borderRadius: "4px",
              }}
            >
              <div className="card-body position-relative p-4">
                {/* Close Button */}
                <div className="position-absolute top-0 end-0 mt-3 me-3">
                  <i
                    className="ti ti-x"
                    style={{
                      background: "#6c757d",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowGuide(false)}
                  ></i>
                </div>

                {/* Heading */}
                <h5 className="fw-bold mb-3">Risk Assessment</h5>

                {/* Paragraph */}
                <p className="mb-3 text-muted" style={{ lineHeight: "1.8" }}>
                  The color code and checking rules will be defined in this
                  section. The excel sheet if provided by the client will have
                  to be configured in this section. The Aupertz risk assessment
                  will be set as default.
                </p>

                <ul
                  className="mb-0 text-muted ps-3"
                  style={{ lineHeight: "1.8", listStyle: "circle" }}
                >
                  <li>
                    Clients can decide whether a particular rule needs to be
                    applied by simply activating the button as on or off.
                  </li>

                  <li>
                    If a rule is not available, the client must request it and
                    it will be coded into the main platform. A request can be
                    sent by contacting us using the support tab.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Table  */}
        <div className="card mt-3 shadow-sm border-0">
          {/* Header
          <div className="card-header bg-white border-bottom">
            <h4 className="card-title mb-0 text-center fw-bold">Risk Assessment</h4>
          </div> */}
          {/* Body */}
          <div className="card-body">
            {/* Role Select */}
            <div className="row">
              <div className="col-md-6">
                <SearchableSelect
                  label="Risk Assessment"
                  name="riskMatrix"
                  value={selectedRisk}
                  options={riskMatrixOptions}
                  onChange={(name, value) => handleRiskChange(value)}
                  hasRowGrid={false}
                  // error={errors?.package}
                  required
                />
              </div>
            </div>
            {/* Routes Section */}
            {selectedRisk && (
              <div className="card mt-4 shadow-sm border-0">
                <div className="card-header bg-white border-bottom">
                  <button className="btn btn-sm btn-outline-danger fw-semibold">
                    All MOVS
                  </button>
                </div>

                <div className="card-body p-0">
                  <Datatable
                    rowKey="key"
                    dataSource={itemList}
                    columns={columns}
                    Selection={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {!selectedRisk && <div className="my-5 py-5"></div>}
      </div>

      <Footer />
    </div>
  );
};

export default RiskMatrix;

// const handleRiskChange = (value: string) => {
//   setSelectedRisk(value);

//   switch (value) {
//     case "criminal":
//       setItemList(criminalData);
//       break;

//     case "education":
//       setItemList(educationData);
//       break;

//     case "address":
//       setItemList(criminalData);
//       break;

//     case "internationalEmployment":
//       setItemList(criminalData);
//       break;

//     case "internationalEducation":
//       setItemList(criminalData);
//       break;

//     default:
//       setItemList([]);
//   }
// };
